import { Button, Divider, Drawer, Image, Modal, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle } from "react-icons/fi";
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import formatTimestamp from "../../utils/timestampConvert";
import getHumanAddress from "../../utils/getHumanAddress";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { db } from "../../firebase";

const IncidentModal = ({ isOpen, onClose, transportOrderId, transportOrderRef, fleetId, driver }) => {
  const { t } = useTranslation();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { hereApiKey } = useGlobalSettings();

  useEffect(() => {
    if (!isOpen || !fleetId || !transportOrderId) return;

    const fetchIncidents = async () => {
      setLoading(true);
      try {
        const incidentsQuery = query(
          collection(db, "fleets", fleetId, "incidents"),
          where("transportOrderId", "==", transportOrderId)
        );
        const incidentsSnapshot = await getDocs(incidentsQuery);
        const data = incidentsSnapshot.docs
          .map((incidentDoc) => ({
            ...incidentDoc.data(),
            id: incidentDoc.id,
            incidentId: incidentDoc.id,
          }))
          .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));

        if (Array.isArray(data)) {
          const incidentsWithAddress = await Promise.all(
            data.map(async (incident) => {
              let humanAddress = t("incidentModal.noLocation");

              if (incident.location?.latitude && incident.location?.longitude) {
                humanAddress = await getHumanAddress(
                  incident.location.latitude,
                  incident.location.longitude,
                  hereApiKey
                );
              }

              return {
                ...incident,
                humanAddress,
              };
            })
          );

          setIncidents(incidentsWithAddress);

          const unreadIncidents = incidentsWithAddress.filter((incident) => !incident.read);
          if (unreadIncidents.length > 0) {
            await Promise.all(
              unreadIncidents.map(async (incident) => {
                await markIncidentAsRead(incident.incidentId);
              })
            );
          }
        } else {
          setIncidents([]);
        }
      } catch (error) {
        console.error("Error fetching incidents:", error);
        setIncidents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidents();
  }, [isOpen, fleetId, transportOrderId, hereApiKey, t]);

  const markIncidentAsRead = async (incidentId) => {
    if (!fleetId || !incidentId) return;

    try {
      const incidentRef = doc(db, "fleets", fleetId, "incidents", incidentId);
      await updateDoc(incidentRef, { read: true });

      setIncidents((prev) =>
        prev.map((incident) =>
          incident.incidentId === incidentId ? { ...incident, read: true } : incident
        )
      );
    } catch (error) {
      console.error("Error updating an incident", error);
    }
  };

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} placement="right" width={520}>
        <div style={{ paddingBottom: 12 }}>
          <Typography.Title level={5} style={{ marginTop: 8 }}>
            {t("incidentModal.title")}: {transportOrderRef || transportOrderId}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t("incidentModal.driver")}: {driver}
          </Typography.Text>
        </div>

        {loading ? (
          <Spin />
        ) : incidents.length === 0 ? (
          <Typography.Text>{t("incidentModal.notFound")}</Typography.Text>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {incidents.map((incident, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  background: "var(--chakra-colors-gandolapp-100)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button
                    size="small"
                    type="primary"
                    icon={<FiAlertTriangle />}
                    onClick={() => markIncidentAsRead(incident.incidentId)}
                  />
                  <Typography.Text strong>{formatTimestamp(incident.timestamp)}</Typography.Text>
                </div>

                <Typography.Paragraph style={{ marginTop: 8 }}>
                  <strong>{t("incidentModal.incident")}:</strong> {incident.message}
                </Typography.Paragraph>

                <Divider />

                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>{t("incidentModal.location")}:</strong> {incident.humanAddress} (
                  {incident.location.latitude}, {incident.location.longitude})
                </Typography.Text>

                <div style={{ marginTop: 8 }}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    {t("incidentModal.photos")}:
                  </Typography.Text>

                  {incident.urls && incident.urls.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                      {incident.urls.map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt={t("incidentModal.imageNumberAlt", { number: i + 1 })}
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedImage(url);
                            setIsImageOpen(true);
                          }}
                        />
                      ))}
                    </div>
                  ) : incident.url ? (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={incident.url}
                        alt={t("incidentModal.imageAlt")}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 200,
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSelectedImage(incident.url);
                          setIsImageOpen(true);
                        }}
                      />
                    </div>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t("incidentModal.noPhoto")}
                    </Typography.Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <Modal open={isImageOpen} onCancel={() => setIsImageOpen(false)} footer={null} centered>
        {selectedImage && (
          <Image
            src={selectedImage}
            alt={t("incidentModal.expandedImageAlt")}
            style={{ maxHeight: "80vh" }}
          />
        )}
      </Modal>
    </>
  );
};

export default IncidentModal;
